import {
  ExpertFilters, ExpertFrontFilters, ExpertIdentifier, ExpertOrderBy,
} from "../models/expert";

import dayjs from "./dayjs";

export interface ExpertOrderings {
  default: string[]; alphabetical: string[]; filtered: string[];
}

export const defaultExpertFilters: ExpertFilters = {
  consultationType: [],
  languages: [],
  skills: [],
  hidden: false,
};

export const defaultExpertFrontFilters: ExpertFrontFilters = {
  expertType: ["AVAILABLE"],
  search: null,
};

export const emptyExpertFrontFilters: ExpertFrontFilters = {
  expertType: [],
  search: null,
};

export const isExpertNew = (createdAt: string) => dayjs(createdAt).isAfter(dayjs().subtract(3, "month"));

const frontFiltersMatchFactory = ({ search, expertType }: ExpertFrontFilters, favoriteSet: Set<string>) => {
  if (expertType?.includes("FAVORITE") && favoriteSet.size === 0) return (expert: ExpertIdentifier) => false;

  const checks = [];
  if (search && search !== "") {
    checks.push(search.match(/^\d{1,3}$/)
      ? (expert: ExpertIdentifier) => expert.expertCode.startsWith(search)
      : (expert: ExpertIdentifier) => expert.pseudo.toLowerCase().includes(search.toLowerCase()));
  }
  if (expertType?.includes("FAVORITE")) {
    checks.push((expert: ExpertIdentifier) => favoriteSet.has(expert.expertUid));
  }
  if (expertType?.includes("AVAILABLE")) {
    checks.push((expert: ExpertIdentifier) => expert.online);
  }
  if (expertType?.includes("NEW")) {
    checks.push((expert: ExpertIdentifier) => isExpertNew(expert.createdAt));
  }
  return (expert: ExpertIdentifier) => checks.every((check) => check(expert));
};

export const getOrderedList = (orderings: ExpertOrderings, identifiers, favoriteSet, consultedSet, expertFrontFilters, expertOrderBy) => {
  const isFiltered = JSON.stringify(expertFrontFilters) !== JSON.stringify(emptyExpertFrontFilters);
  const matchFrontFilters = frontFiltersMatchFactory(expertFrontFilters, favoriteSet);

  const filteredSet = new Set(orderings.filtered);
  if (expertOrderBy === ExpertOrderBy.DEFAULT) {
    const { priorityList, onlineList, offlineList } = orderings.default.reduce((aggr, expertUid) => {
      if (!filteredSet.has(expertUid)) return aggr;
      const identifier = identifiers[expertUid];
      if (!identifier) return aggr;
      if (isFiltered && !matchFrontFilters(identifier)) return aggr;
      if (identifier.online) {
        if (favoriteSet.has(expertUid) || consultedSet.has(expertUid)) return { ...aggr, priorityList: [...aggr.priorityList, identifier] };
        return { ...aggr, onlineList: [...aggr.onlineList, identifier] };
      }
      return { ...aggr, offlineList: [...aggr.offlineList, identifier] };
    }, { priorityList: [], onlineList: [], offlineList: [] });
    return [...priorityList, ...onlineList, ...offlineList];
  } if (expertOrderBy === ExpertOrderBy.ALPHABETICAL) {
    return orderings.alphabetical.reduce((aggr, expertUid) => {
      if (!filteredSet.has(expertUid)) return aggr;
      const identifier = identifiers[expertUid];
      if (!identifier) return aggr;
      if (isFiltered && !matchFrontFilters(identifier)) return aggr;
      return [...aggr, identifier];
    }, []);
  }
  return orderings.filtered.reduce((aggr, expertUid) => {
    const identifier = identifiers[expertUid];
    if (!identifier) return aggr;
    if (isFiltered && !matchFrontFilters(identifier)) return aggr;
    return [...aggr, identifier];
  }, []);
};
